import {
  cancelCampaignService,
  createCampaignService,
  getCampaignByIdService,
  getCampaignsService,
  getPushDashboardStatsService,
  previewAudienceService,
  sendCampaignService,
  sendToUserService,
} from "@/services/push.service";
import type {CampaignFilters, CreateCampaignDto, PreviewAudienceDto, SendToUserDto} from "@/types/push.types";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

const CAMPAIGNS_KEY = ["push", "campaigns"] as const;
const DASHBOARD_KEY = ["push", "dashboard"] as const;

// Dashboard stats
export const usePushDashboard = () =>
  useQuery({
    queryKey: DASHBOARD_KEY,
    queryFn: async () => {
      const {data} = await getPushDashboardStatsService();
      if (data === undefined) throw new Error("No data");
      return data;
    },
    staleTime: 30_000,
  });

// Campaign list
export const useCampaigns = (filters: CampaignFilters = {}) =>
  useQuery({
    queryKey: [...CAMPAIGNS_KEY, filters],
    queryFn: async () => {
      const {data} = await getCampaignsService(filters);
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

// Campaign detail — polls every 3s while status is "sending" (Firebase realtime stats)
export const useCampaign = (id: string) => {
  const query = useQuery({
    queryKey: [...CAMPAIGNS_KEY, id],
    queryFn: async () => {
      const {data} = await getCampaignByIdService(id);
      if (data === undefined) throw new Error("No data");
      return data;
    },
    enabled: !!id,
    refetchInterval: query => {
      return query.state.data?.status === "sending" ? 3_000 : false;
    },
  });
  return query;
};

// Preview audience count (no caching, always fresh)
export const usePreviewAudience = () =>
  useMutation({
    mutationFn: (body: PreviewAudienceDto) => previewAudienceService(body),
  });

// Create campaign
export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCampaignDto) => createCampaignService(body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: CAMPAIGNS_KEY});
      queryClient.invalidateQueries({queryKey: DASHBOARD_KEY});
    },
  });
};

// Send campaign
export const useSendCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => sendCampaignService(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: CAMPAIGNS_KEY});
      queryClient.invalidateQueries({queryKey: DASHBOARD_KEY});
    },
  });
};

// Cancel campaign
export const useCancelCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => cancelCampaignService(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: CAMPAIGNS_KEY});
      queryClient.invalidateQueries({queryKey: DASHBOARD_KEY});
    },
  });
};

// Send notification to individual user
export const useSendToUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SendToUserDto) => sendToUserService(body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: CAMPAIGNS_KEY});
      queryClient.invalidateQueries({queryKey: DASHBOARD_KEY});
    },
  });
};
